import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../../api/axios';
import { IApiResponseError, IApiState } from '../../../../types/api';
import { RootState } from '../../..';
import { IAutoDebitConfiguration } from '../types';
import { getApiErrorMessage, getSavingsAuthConfig } from '../utils';

interface ISearchAutoDebitConfigsRequest {
  pageNumber?: number;
  pageSize?: number;
  searchParams?: {
    savingsAccountNumber?: string;
    autoDebitFrequency?: string;
  };
}

interface ISearchAutoDebitConfigsResponse {
  status?: boolean;
  message?: string;
  totalCount?: number;
  data?: IAutoDebitConfiguration[];
  errors?: IApiResponseError[];
}

const initialState: IApiState<ISearchAutoDebitConfigsResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const searchAutoDebitConfigsAsync = createAsyncThunk(
  'savings/autoDebit/search',
  async (
    { pageNumber = 1, pageSize = 10, searchParams = {} }: ISearchAutoDebitConfigsRequest,
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as RootState;

      const { data } = await api.post<ISearchAutoDebitConfigsResponse>(
        '/savings/auto-debit-configurations/search',
        { pageNumber, pageSize, searchParams },
        getSavingsAuthConfig(state),
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<IApiResponseError>;
      return rejectWithValue(error.response?.data);
    }
  },
);

export const searchAutoDebitConfigsSlice = createSlice({
  name: 'savings/autoDebit/search',
  initialState,
  reducers: {
    resetSearchAutoDebitConfigs(state) {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchAutoDebitConfigsAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(searchAutoDebitConfigsAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload as ISearchAutoDebitConfigsResponse;
      })
      .addCase(searchAutoDebitConfigsAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.error = getApiErrorMessage(action.payload);
      });
  },
});

export const { resetSearchAutoDebitConfigs } =
  searchAutoDebitConfigsSlice.actions;

export default searchAutoDebitConfigsSlice.reducer;
